/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useRef, useState } from "react";
import { Row, Col, Button, Card, Badge } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";

/* API */
import { aircallParseSheet, aircallTriggerCalls } from "../../../service/api";

// ─── Status badge ─────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  if (!status) return <span style={{ color: "#aaa" }}>—</span>;
  const map = {
    queued:    { bg: "#d1fae5", color: "#065f46", label: "✅ Queued" },
    duplicate: { bg: "#fef3c7", color: "#92400e", label: "🔁 Already called today" },
    skipped:   { bg: "#f3f4f6", color: "#374151", label: "⏭ Skipped" },
    error:     { bg: "#fee2e2", color: "#991b1b", label: "❌ Error" },
    calling:   { bg: "#dbeafe", color: "#1e40af", label: "📞 Calling…" },
  };
  const s = map[status] || { bg: "#f3f4f6", color: "#374151", label: status };
  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        borderRadius: 6,
        padding: "3px 10px",
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </span>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const AircallPage = () => {
  const fileInputRef = useRef(null);

  const [stage, setStage]       = useState("upload");   // "upload" | "preview" | "done"
  const [fileName, setFileName] = useState("");
  const [leads, setLeads]       = useState([]);          // parsed rows (with callStatus)
  const [calling, setCalling]   = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // ── Upload handler ─────────────────────────────────────────────────────────
  const handleFile = async (file) => {
    if (!file) return;
    const allowed = [".csv", ".xlsx", ".xls"];
    const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
    if (!allowed.includes(ext)) {
      toast.error("Please upload a .csv, .xlsx, or .xls file.");
      return;
    }

    setFileName(file.name);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await aircallParseSheet(formData);
      if (res?.status && Array.isArray(res?.data)) {
        setLeads(res.data.map((r) => ({ ...r, callStatus: null, callMessage: "" })));
        setStage("preview");
        toast.success(res.message);
      } else {
        toast.error(res?.message || "Failed to parse file.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Upload failed.");
    }
  };

  const onFileChange = (e) => handleFile(e.target.files[0]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  // ── Call All handler ───────────────────────────────────────────────────────
  const handleCallAll = async () => {
    setCalling(true);

    // Set all rows to "calling" state for immediate visual feedback
    setLeads((prev) =>
      prev.map((l) => ({ ...l, callStatus: "calling", callMessage: "" }))
    );

    try {
      const res = await aircallTriggerCalls(leads);
      if (res?.status && Array.isArray(res?.data)) {
        setLeads(res.data.map((r) => ({
          ...r,
          callStatus:  r.callStatus,
          callMessage: r.callMessage,
        })));
        setStage("done");
        toast.success(res.message);
      } else {
        toast.error(res?.message || "Something went wrong.");
        setLeads((prev) =>
          prev.map((l) => ({ ...l, callStatus: null, callMessage: "" }))
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to trigger calls.");
      setLeads((prev) =>
        prev.map((l) => ({ ...l, callStatus: null, callMessage: "" }))
      );
    } finally {
      setCalling(false);
    }
  };

  const handleReset = () => {
    setLeads([]);
    setFileName("");
    setStage("upload");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── Columns ───────────────────────────────────────────────────────────────
  const columns = [
    {
      name: "Name",
      grow: 2,
      selector: (row) => (
        <div className="user-detail">
          <div className="avatar" style={{ background: "#4f46e5" }}>
            {(row.firstName?.[0] || "?").toUpperCase()}
            {(row.lastName?.[0] || "").toUpperCase()}
          </div>
          <h5>{row.firstName} {row.lastName}</h5>
        </div>
      ),
    },
    {
      name: "Phone",
      selector: (row) => <span className="light-text">{row.phone || "—"}</span>,
    },
    {
      name: "Email",
      grow: 2,
      selector: (row) => <span className="light-text">{row.email || "—"}</span>,
    },
    {
      name: "Tag",
      selector: (row) => (
        row.tag ? (
          <span
            style={{
              background: "#ede9fe",
              color: "#5b21b6",
              borderRadius: 6,
              padding: "2px 8px",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {row.tag}
          </span>
        ) : "—"
      ),
    },
    {
      name: "Call Status",
      grow: 2,
      selector: (row) => (
        <div>
          <StatusBadge status={row.callStatus} />
          {row.callMessage && row.callStatus === "error" && (
            <div style={{ fontSize: 11, color: "#991b1b", marginTop: 2 }}>
              {row.callMessage}
            </div>
          )}
        </div>
      ),
    },
  ];

  // ── Stats ─────────────────────────────────────────────────────────────────
  const statQueued    = leads.filter((l) => l.callStatus === "queued").length;
  const statDuplicate = leads.filter((l) => l.callStatus === "duplicate").length;
  const statError     = leads.filter((l) => l.callStatus === "error").length;

  return (
    <section className="users">
      {/* BACKGROUND BANNER */}
      <div className="bg-blue-banner" />

      <div className="container">
        {/* PAGE HEADING */}
        <div className="page-head">
          <Row className="align-center">
            <Col lg="8" sm="12">
              <h1>Aircall Outbound Calls</h1>
              <p style={{ fontSize: 15 }}>
                Upload a CSV or Excel sheet of leads and trigger outbound calls via Aircall.
              </p>
            </Col>
            <Col lg="4" sm="12" style={{ textAlign: "right" }}>
              {stage !== "upload" && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleReset}
                  style={{ marginRight: 8 }}
                  id="aircall-reset-btn"
                >
                  ↩ Upload new file
                </Button>
              )}
              {stage === "preview" && (
                <Button
                  className="proceed-btn"
                  id="aircall-call-all-btn"
                  disabled={calling || leads.length === 0}
                  onClick={handleCallAll}
                >
                  {calling ? "📞 Calling…" : `📞 Call All (${leads.length})`}
                </Button>
              )}
            </Col>
          </Row>
        </div>

        {/* ── UPLOAD STAGE ── */}
        {stage === "upload" && (
          <div className="users-table">
            <Card className="users-list">
              <Card.Body style={{ padding: "48px 32px" }}>
                <div
                  id="aircall-drop-zone"
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    border: `2px dashed ${dragOver ? "#4f46e5" : "#c7d2fe"}`,
                    borderRadius: 16,
                    padding: "64px 32px",
                    textAlign: "center",
                    cursor: "pointer",
                    background: dragOver ? "#eef2ff" : "#f5f7ff",
                    transition: "all 0.2s",
                  }}
                >
                  <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
                  <h4 style={{ color: "#4f46e5", marginBottom: 8 }}>
                    Drag & drop your leads file here
                  </h4>
                  <p style={{ color: "#6b7280", marginBottom: 24 }}>
                    Supported formats: <strong>.csv</strong>, <strong>.xlsx</strong>, <strong>.xls</strong>
                  </p>
                  <Button className="proceed-btn" id="aircall-browse-btn">
                    Browse file
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    style={{ display: "none" }}
                    id="aircall-file-input"
                    onChange={onFileChange}
                  />
                </div>

                {/* Column format hint */}
                <div style={{ marginTop: 32 }}>
                  <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 8 }}>
                    <strong>Expected column headers:</strong>
                  </p>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {[
                      "Person - Name *",
                      "First Name",
                      "Last Name",
                      "Person - Phone (suggested)",
                      "Person - Email (suggested)",
                      "Tag",
                    ].map((col) => (
                      <span
                        key={col}
                        style={{
                          background: "#e0e7ff",
                          color: "#3730a3",
                          borderRadius: 6,
                          padding: "3px 10px",
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {col}
                      </span>
                    ))}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>
        )}

        {/* ── PREVIEW / DONE STAGE ── */}
        {(stage === "preview" || stage === "done") && (
          <div className="users-table">
            {/* Stats row (only when done) */}
            {stage === "done" && (
              <Row style={{ marginBottom: 20 }}>
                {[
                  { label: "Queued", count: statQueued,    bg: "#d1fae5", color: "#065f46" },
                  { label: "Already called", count: statDuplicate, bg: "#fef3c7", color: "#92400e" },
                  { label: "Errors", count: statError,    bg: "#fee2e2", color: "#991b1b" },
                ].map((s) => (
                  <Col key={s.label} lg="4" sm="12" style={{ marginBottom: 12 }}>
                    <Card style={{ background: s.bg, border: "none", borderRadius: 12 }}>
                      <Card.Body style={{ padding: "16px 24px" }}>
                        <h2 style={{ color: s.color, margin: 0 }}>{s.count}</h2>
                        <p style={{ color: s.color, margin: 0, fontWeight: 600, fontSize: 13 }}>
                          {s.label}
                        </p>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}

            <Card className="users-list">
              <Card.Header className="users-list-header">
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontWeight: 600, color: "#374151" }}>
                    📄 {fileName}
                  </span>
                  <span
                    style={{
                      background: "#e0e7ff",
                      color: "#3730a3",
                      borderRadius: 20,
                      padding: "2px 10px",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {leads.length} lead{leads.length !== 1 ? "s" : ""}
                  </span>
                </div>
              </Card.Header>
              <Card.Body className="users-list-body">
                <DataTable
                  columns={columns}
                  data={leads}
                  pagination
                  highlightOnHover
                  striped
                />
              </Card.Body>
            </Card>
          </div>
        )}
      </div>
    </section>
  );
};

export default AircallPage;
